import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bell, X, Check, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { notificationsApi } from '../../api';
import SlideOver from '../shared/SlideOver';
import DEFERRED_SURFACES from '../../config/deferredSurfaces';

const PANEL_PAGE_SIZE = 20;

function formatRelative(iso, t) {
  if (!iso) return '';
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return '';
  const diffMin = Math.max(0, Math.round((Date.now() - created) / 60_000));
  if (diffMin < 1) return t('panel.justNow');
  if (diffMin < 60) return t('panel.minutesAgo', { count: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('panel.hoursAgo', { count: diffHr });
  const diffDay = Math.floor(diffHr / 24);
  return t('panel.daysAgo', { count: diffDay });
}

// QA #7 — slide-over panel for the TopBar notification bell. Uses
// DESIGN_DNA §3.6 (slide-over with isRTL-mirrored positioning) and
// pulls from the existing /notifications endpoints.
export default function NotificationsPanel({ open, onClose }) {
  const { t } = useTranslation('notification');
  const queryClient = useQueryClient();
  const closeButtonRef = useRef(null);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['notifications', 'list'],
    queryFn: async ({ pageParam }) => {
      // The bell panel shows the actionable (unread) set so its count matches
      // the TopBar bell badge — both derive from the SAME server-side unread
      // total (_unread_visible_filter), so they can never diverge.
      const res = await notificationsApi.list({
        page: pageParam,
        perPage: PANEL_PAGE_SIZE,
        unreadOnly: true,
      });
      // list_response emits { data: [...items], pagination: {...} } — the items
      // are an ARRAY under `data`, NOT an object with `.items`. httpClient
      // surfaces that body verbatim as res.data, so the array is res.data.data.
      // Peel exactly one layer: the old `res.data?.data || res.data` collapsed
      // the array onto itself and the follow-on `data?.items` lookup then always
      // yielded [] — the panel rendered "all caught up" for every user while the
      // badge showed a non-zero count (M9: the PD-001 deferral notice hidden).
      const body = res.data ?? {};
      const list = Array.isArray(body.data) ? body.data : [];
      const pagination = body.pagination || {};
      return {
        items: list,
        total: Number(pagination.total) || list.length,
        page: Number(pagination.page) || pageParam,
        pages: Number(pagination.pages) || 1,
      };
    },
    // gh-issues-hardening (round-2 #1): a user who accumulates more than one
    // page of unread notifications previously had no way to reach anything
    // past the newest 20 short of "mark all read" — which discards the
    // older backlog unread without ever showing it. Paginate forward on the
    // SAME server-side unread query so the whole backlog stays reachable.
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
    enabled: open,
    staleTime: 30_000,
  });

  const items = data?.pages?.flatMap((p) => p.items) || [];
  // Server already scoped the query to this user's unread set; `total` is the
  // authoritative unread count and equals the TopBar bell badge — it comes
  // from the first page's pagination metadata, which is stable across pages
  // (same underlying unread filter). The panel keys "unread" on `n.read` (the
  // per-user flag Notification.to_dict() emits) — the serialized row has no
  // `read_at` field, so the previous `!n.read_at` test was always true.
  const unreadTotal = data?.pages?.[0]?.total ?? items.length;

  const markRead = useMutation({
    mutationFn: (id) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => {
      toast.error(t('panel.markReadError'));
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => {
      toast.error(t('panel.markAllReadError'));
    },
  });

  return (
    <SlideOver
      isOpen={open}
      onClose={onClose}
      ariaLabel={t('panel.title')}
      width="standard"
      showHeader={false}
    >
      <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4 shrink-0">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-900">
              {t('panel.title')}
            </h2>
            {unreadTotal > 0 && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium"
                aria-live="polite"
              >
                {t('panel.unreadCount', { count: unreadTotal })}
              </span>
            )}
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            aria-label={t('cancel')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto" aria-live="polite">
          {isLoading && (
            <div className="px-5 py-8 text-center text-sm text-slate-500">
              {t('panel.loading')}
            </div>
          )}
          {isError && (
            <div className="px-5 py-8 text-center text-sm text-rose-600">
              {t('panel.error')}
            </div>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-slate-500">
              {t('panel.empty')}
            </div>
          )}
          {!isLoading && !isError && items.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const isUnread = !n.read;
                return (
                  <li
                    key={n.id}
                    className={`px-5 py-4 ${isUnread ? 'bg-emerald-50/40' : 'bg-white'}`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${
                          isUnread ? 'bg-emerald-500' : 'bg-slate-300'
                        }`}
                        aria-hidden="true"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 line-clamp-2">
                          {n.title || n.message || ''}
                        </p>
                        {n.message && n.title && n.message !== n.title && (
                          <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                            {n.message}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {formatRelative(n.created_at, t)}
                        </p>
                      </div>
                      {isUnread && (
                        <button
                          onClick={() => markRead.mutate(n.id)}
                          disabled={markRead.isPending}
                          className="p-1 text-slate-400 hover:text-emerald-600 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                          aria-label={t('panel.markAllRead')}
                        >
                          <Check size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {!isLoading && !isError && hasNextPage && (
            <div className="px-5 py-3">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
              >
                {isFetchingNextPage ? t('panel.loadingMore') : t('panel.loadMore')}
              </button>
            </div>
          )}
        </div>

        <footer className={`border-t border-slate-200 px-5 py-3 flex items-center shrink-0 ${DEFERRED_SURFACES.SHOW_BREACH_NOTIFICATIONS ? 'justify-between' : 'justify-center'}`}>
          <button
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || unreadTotal === 0}
            className="text-sm text-emerald-700 hover:text-emerald-800 disabled:text-slate-400 disabled:cursor-not-allowed font-medium"
          >
            {t('panel.markAllRead')}
          </button>
          {/* GH #166 — the Breach Notifications module is deferred, so its
              route is unregistered; gate this link so the panel never
              dangles a "View all" affordance into an unrouted path. Flip
              DEFERRED_SURFACES.SHOW_BREACH_NOTIFICATIONS to restore. */}
          {DEFERRED_SURFACES.SHOW_BREACH_NOTIFICATIONS && (
            <Link
              to="/breach-notifications"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            >
              {t('panel.viewAll')}
              <ExternalLink size={14} />
            </Link>
          )}
        </footer>
    </SlideOver>
  );
}
